import React, { useState, useEffect } from 'react';
import { DoorOpen, DoorClosed, Lock, Unlock, Package, Clock, User } from 'lucide-react';

const LockerSimulator: React.FC = () => {
  const [lockerStates, setLockerStates] = useState<Record<string, boolean>>({});
  const [lockerDetails, setLockerDetails] = useState<Record<string, {
    isOccupied: boolean;
    parcelTrackingNumber?: string;
    recipientPhone?: string;
    placementTime?: string;
  }>>({});
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'disconnected'>('disconnected');
  const [error, setError] = useState<string>('');

  useEffect(() => {
    const ws = new WebSocket('ws://localhost:5210/lockerSimulator');

    ws.onopen = () => {
      setConnectionStatus('connected');
      setError('');
    };
    ws.onclose = () => setConnectionStatus('disconnected');
    ws.onerror = () => setError('Failed to connect to simulator backend');

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'initialState') {
          setLockerStates(data.lockers);
          // Simulate some lockers being occupied
          const details: Record<string, any> = {};
          Object.keys(data.lockers).forEach(lockerId => {
            if (Math.random() > 0.5) {
              details[lockerId] = {
                isOccupied: true,
                parcelTrackingNumber: `PKT${Math.floor(Math.random() * 1000000)}`,
                recipientPhone: '065' + Math.floor(Math.random() * 1000000),
                placementTime: new Date(Date.now() - Math.random() * 86400000 * 3).toISOString()
              };
            } else {
              details[lockerId] = {
                isOccupied: false
              };
            }
          });
          setLockerDetails(details);
        } else if (data.type === 'stateUpdate') {
          setLockerStates(prev => ({
            ...prev,
            [data.lockerId]: data.isOpen
          }));
        }
      } catch (err) {
        setError('Invalid data received from server');
      }
    };

    return () => ws.close();
  }, []);

  const handleLockerClick = (lockerId: string) => {
    if (lockerStates[lockerId]) {
      const ws = new WebSocket('ws://localhost:5210/lockerSimulator');
      ws.onopen = () => {
        ws.send(JSON.stringify({
          command: 'close',
          lockerId
        }));
        ws.close();
      };
    }
  };

  const getTimeSince = (dateString: string) => {
    const diff = Date.now() - new Date(dateString).getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <div className="bg-secondary p-6 shadow-lg">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <h1 className="text-4xl font-bold">
                <span className="text-primary">Locker</span>
                {' '}Hardware Simulator
              </h1>
              <div className="flex items-center space-x-2 ml-6">
                <div className={`w-3 h-3 rounded-full ${
                  connectionStatus === 'connected' ? 'bg-green-500' : 'bg-red-500'
                }`} />
                <span className="text-sm font-medium">
                  {connectionStatus === 'connected' ? 'Connected' : 'Disconnected'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto p-6">
        {error && (
          <div className="mb-6 bg-red-500/10 border border-red-500 text-red-500 p-4 rounded-lg">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6">
          {Object.entries(lockerStates).length === 0 ? (
            <div className="col-span-full text-center py-20 text-xl text-gray-400">
              Waiting for locker data...
            </div>
          ) : (
            Object.entries(lockerStates).map(([lockerId, isOpen]) => {
              const details = lockerDetails[lockerId];
              return (
                <div
                  key={lockerId}
                  onClick={() => handleLockerClick(lockerId)}
                  className={`
                    p-6 rounded-xl shadow-lg cursor-pointer transition-all
                    ${isOpen 
                      ? 'bg-red-500/10 hover:bg-red-500/20' 
                      : 'bg-green-500/10 hover:bg-green-500/20'
                    }
                  `}
                >
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-2xl font-bold">Locker {lockerId}</h2>
                    {isOpen ? (
                      <DoorOpen className="text-red-500 h-8 w-8" />
                    ) : (
                      <DoorClosed className="text-green-500 h-8 w-8" />
                    )}
                  </div>

                  <div className="flex items-center space-x-3 mb-4">
                    <div className={`p-3 rounded-lg ${
                      isOpen ? 'bg-red-500/20' : 'bg-green-500/20'
                    }`}>
                      {isOpen ? (
                        <Unlock className="text-red-500 h-6 w-6" />
                      ) : (
                        <Lock className="text-green-500 h-6 w-6" />
                      )}
                    </div>
                    <span className={`text-lg font-medium ${
                      isOpen ? 'text-red-500' : 'text-green-500'
                    }`}>
                      {isOpen ? 'Open' : 'Closed'}
                    </span>
                  </div>

                  {details?.isOccupied && (
                    <div className="space-y-2 border-t pt-4 mt-4">
                      <div className="flex items-center space-x-2 text-sm">
                        <Package className="h-4 w-4" />
                        <span>Parcel: {details.parcelTrackingNumber}</span>
                      </div>
                      <div className="flex items-center space-x-2 text-sm">
                        <User className="h-4 w-4" />
                        <span>For: {details.recipientPhone}</span>
                      </div>
                      <div className="flex items-center space-x-2 text-sm">
                        <Clock className="h-4 w-4" />
                        <span>In locker for: {getTimeSince(details.placementTime!)}</span>
                      </div>
                    </div>
                  )}

                  {!details?.isOccupied && (
                    <div className="border-t pt-4 mt-4">
                      <span className="text-sm text-gray-400">Available</span>
                    </div>
                  )}

                  {isOpen && (
                    <p className="mt-4 text-sm text-gray-400">
                      Click to close the locker
                    </p>
                  )}
                </div>
              )
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default LockerSimulator;